<template>
<div class="m-0 pt-2 lg:max-w-[1600px] lg:mx-auto lg:px-4 xl:px-8">
    <div
        v-if="!sliderReady"
        class="relative w-full h-48 lg:h-[min(70vh,28rem)] xl:h-[min(75vh,32rem)] 2xl:h-[min(78vh,36rem)] bg-gray-100 rounded-none lg:rounded-2xl"
        aria-hidden="true" />
    <div
        v-else-if="slides.length > 0"
        class="relative w-full h-48 lg:h-[min(70vh,28rem)] xl:h-[min(75vh,32rem)] 2xl:h-[min(78vh,36rem)] overflow-hidden m-0 p-0 box-border rounded-none lg:rounded-2xl"
        style="line-height: 0; font-size: 0;"
        @touchstart="onTouchStart"
        @touchmove="onTouchMove"
        @touchend="onTouchEnd">
        <a
            v-for="(slide, index) in slides"
            :key="slideKey(index, slide)"
            :href="slide.target?.href || '#categories-sticky-trigger'"
            :rel="slide.target?.type === 'external' ? 'noopener noreferrer' : undefined"
            :class="['absolute top-0 left-0 w-full h-full transform transition-transform duration-1000 cursor-pointer', getSlideClass(index)]"
            style="padding: 0 10px;"
            :aria-label="slide.alt || 'عرض البانر'"
            @click="onSlideClick($event, slide, index)">
            <img
                :src="slide.src"
                :alt="slide.alt"
                width="1280"
                height="720"
                :fetchpriority="index === currentIndex ? 'high' : 'low'"
                :loading="index === currentIndex ? 'eager' : 'lazy'"
                class="w-full h-full object-cover block m-0 p-0 align-bottom rounded-lg pointer-events-none"
                decoding="async"
                draggable="false" />
        </a>
    </div>
</div>
</template>

<script setup>
import mainService from "@/service/mainService";
import { sliderImageUrl } from "@/helpers/imageUrls.js";
import { normalizeOdooChar, resolveSliderTarget } from "@/helpers/sliderLink.js";
import { pushEvent } from "@/helpers/analytics.js";
import { ref, onMounted, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";

/** Do not block the page with a placeholder; cap Odoo wait so catalog/API can proceed. */
const SLIDER_FETCH_TIMEOUT_MS = 6000;
const DEFAULT_ALT = "بانر العروض — طازج الغذائية";

const router = useRouter();
const slides = ref([]);
/** False on server and during hydration; flips in onMounted so both render the placeholder first. */
const sliderReady = ref(false);
const currentIndex = ref(0);
const touchStartX = ref(0);
const touchEndX = ref(0);

let autoSlideTimer = null;
let ignoreNextClick = false;

function slideKey(index, slide) {
    const tail = slide?.imageId != null ? String(slide.imageId) : (slide?.src || String(index));
    return `${index}-${tail}`;
}

function mapSlide(item) {
    const alt = normalizeOdooChar(item?.note) || DEFAULT_ALT;
    const target = resolveSliderTarget(item?.url);
    if (item?.image_id) {
        const src = sliderImageUrl(item.image_id);
        return src ? { imageId: item.image_id, src, alt, target } : null;
    }
    if (item?.image) {
        return { imageId: null, src: `data:image/jpeg;base64,${item.image}`, alt, target };
    }
    return null;
}

async function loadImages() {
    try {
        const slidersData = await Promise.race([
            mainService.getSlider(),
            new Promise((_, reject) => {
                setTimeout(() => reject(new Error("slider-timeout")), SLIDER_FETCH_TIMEOUT_MS);
            }),
        ]);
        const list = slidersData?.data?.result;
        if (
            slidersData?.success &&
            Array.isArray(list) &&
            list.length > 0
        ) {
            const serverSlides = list.map(mapSlide).filter(Boolean);
            if (serverSlides.length > 0) {
                slides.value = serverSlides;
            }
        }
    } catch {
        /* No local fallback — avoids stale bundled slider1/slider2 flash */
    }
}

const getSlideClass = (index) => {
    if (index === currentIndex.value) {
        return "translate-x-0";
    }
    if (index < currentIndex.value) {
        return "-translate-x-full";
    }
    return "translate-x-full";
};

const onTouchStart = (event) => {
    const x = event.touches[0].clientX;
    touchStartX.value = x;
    touchEndX.value = x;
};

const onTouchMove = (event) => {
    touchEndX.value = event.touches[0].clientX;
};

const onTouchEnd = () => {
    const count = slides.value.length;
    if (count === 0) {
        return;
    }
    const deltaX = touchEndX.value - touchStartX.value;
    if (deltaX > 50) {
        ignoreNextClick = true;
        currentIndex.value = (currentIndex.value - 1 + count) % count;
    } else if (deltaX < -50) {
        ignoreNextClick = true;
        currentIndex.value = (currentIndex.value + 1) % count;
    }
};

function onSlideClick(event, slide, index) {
    if (ignoreNextClick) {
        event.preventDefault();
        ignoreNextClick = false;
        return;
    }
    event.preventDefault();
    pushEvent("select_promotion", {
        creative_name: "home_slider",
        creative_slot: String(index),
        promotion_id: slide.imageId != null ? String(slide.imageId) : undefined,
        promotion_name: slide.alt,
    });
    if (slide?.target?.type === "internal") {
        router.push(slide.target.path);
        return;
    }
    if (slide?.target?.type === "external") {
        window.location.assign(slide.target.href);
        return;
    }
    document.getElementById("categories-sticky-trigger")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
    });
}

function startAutoSlide() {
    autoSlideTimer = setInterval(() => {
        const count = slides.value.length;
        if (count > 1) {
            currentIndex.value = (currentIndex.value + 1) % count;
        }
    }, 5000);
}

onMounted(() => {
    sliderReady.value = true;
    void loadImages().then(() => {
        if (slides.value.length > 1) {
            startAutoSlide();
        }
    });
});

onBeforeUnmount(() => {
    if (autoSlideTimer) {
        clearInterval(autoSlideTimer);
    }
});
</script>
